<script setup lang="ts">
  import { ref, computed } from 'vue'
  import type { MessageProps } from './types'
  import Icon from '../ui/Icon.vue'
  import type { IconName } from '../ui/types'

  const props = withDefaults(defineProps<MessageProps>(), {
    severity: 'success',
    message: '',
    title: '',
    closeable: true,
  })

  const isVisible = ref(true)

  const close = () => {
    if (props.closeable) {
      isVisible.value = false
    }
  }

  const iconBackgroundColor = computed(() => {
    const colors = {
      error: '#FEE2E2',
      success: '#DCFCE7',
      warning: '#FEF3C7',
      info: '#DBEAFE',
    }
    return colors[props.severity]
  })

  const iconStrokeColor = computed(() => {
    const colors = {
      error: '#DC2626',
      success: '#16A34A',
      warning: '#D97706',
      info: '#2563EB',
    }
    return colors[props.severity]
  })

  const defaultIconBySeverity = computed(() => {
    const icons = {
      error: 'close-circle',
      success: 'check-circle',
      warning: 'info',
      info: 'info',
    }
    return icons[props.severity]
  })
</script>

<template>
  <div v-if="isVisible" class="message" :class="[`message--${severity}`]">
    <div class="message__content">
      <div class="message__icon" :style="{ marginTop: title ? '-12px' : '0' }">
        <Icon
          :name="icon || (defaultIconBySeverity as unknown as IconName)"
          :size="16"
          :color="iconStrokeColor"
        />
      </div>
      <div class="message__text">
        <p v-if="title" class="message__title">
          {{ title }}
        </p>

        <slot></slot>
      </div>
    </div>
    <button v-if="closeable" class="message__close" @click="close">
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11 1L1 11M1 1L11 11"
          :stroke="iconStrokeColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<style lang="scss" scoped>
  .message {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 10px;
    gap: 12px;
    max-width: 385px;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

    &--error {
      background: #fef2f2;

      .message__close path,
      .message__text,
      .message__title {
        stroke: #ef4444;
        color: #ef4444;
      }
    }

    &--success {
      background: #f0fdf4;

      .message__close path,
      .message__title,
      .message__text {
        stroke: #22c55e;
        color: #22c55e;
      }
    }

    &--warning {
      background: #fffbeb;

      .message__text,
      .message__title,
      .message__close path {
        color: #b45309;
        stroke: #b45309;
        line-height: 1.5;
      }
    }

    &--info {
      background: #eff6ff;

      .message__text,
      .message__title,
      .message__close path {
        color: #3b82f6;
        stroke: #3b82f6;
      }
    }

    &__content {
      display: flex;
      align-items: center;
      gap: 12px;
      color: #1f2937;
      font-size: 14px;
      line-height: 20px;
    }

    &__title {
      font-weight: 500;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: #fef3c7;
      flex-shrink: 0;
    }

    &__close {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        opacity: 0.8;
      }
    }
  }
</style>
